import {
  MODULE_NAMESPACES,
  RELEASED_LOCALES,
  SOURCE_MESSAGES,
  TRANSLATION_ALLOWLIST,
  catalogueCompleteness,
  formatMessage,
  loadCatalogue,
  localeDirection,
  normaliseUiLocale,
  type Messages,
  type Namespace,
  type UiLocale,
} from '@his/shared';

const STORAGE_KEY = 'his.uiLocale';

export type ModuleNamespace = (typeof MODULE_NAMESPACES)[number];

export type ModuleCatalogues = Partial<Record<ModuleNamespace, Messages>>;

let moduleLocale: UiLocale = 'en';

let moduleMessages: ModuleCatalogues = {};

export function setModuleCatalogues(locale: UiLocale, messages: ModuleCatalogues): void {
  moduleLocale = locale;
  moduleMessages = messages;
}

export function tr(namespace: ModuleNamespace, key: string, params?: Record<string, unknown>): string {
  const translated = moduleMessages[namespace]?.[key];
  const message = translated ?? SOURCE_MESSAGES[namespace][key];
  if (message === undefined) return key;
  if (!params && !/[{'#]/.test(message)) return message;
  const values = params ? Object.fromEntries(Object.entries(params).map(([name, value]) => [name, String(value)])) : {};
  return formatMessage(message, values, translated === undefined ? 'en' : moduleLocale);
}

let serverKeys: Map<string, string> | null = null;

function serverKeyOf(message: string): string | undefined {
  if (!serverKeys) serverKeys = new Map(Object.entries(SOURCE_MESSAGES.server).map(([key, english]) => [english, key]));
  return serverKeys.get(message.trim());
}

export function translateServerMessage(message: string | undefined): string | null {
  const target = moduleMessages.server;
  if (!message || !target) return null;
  const key = serverKeyOf(message);
  if (key && target[key]) return target[key];
  const missing = /^(.+) was not found$/.exec(message.trim());
  const entityKey = missing ? serverKeyOf(missing[1]!) : undefined;
  const entity = entityKey ? target[entityKey] : undefined;
  return entity && errorTranslator ? errorTranslator('not_found', { entity }) : null;
}

type ErrorTranslator = (key: string, params?: Record<string, string | number>) => string | null;

let errorTranslator: ErrorTranslator | null = null;

type ValueTranslator = (value: string, english: string, group?: string) => string | null;

let valueTranslator: ValueTranslator | null = null;

let sourceValueTranslator: ValueTranslator | null = null;

export function setValueTranslator(translator: ValueTranslator | null): void {
  valueTranslator = translator;
}

function englishValueTranslator(): ValueTranslator {
  sourceValueTranslator ??= enumValueTranslator(SOURCE_MESSAGES.enum);
  return sourceValueTranslator;
}

export function translateEnumValue(value: string, english: string, group?: string): string | null {
  return (valueTranslator ?? englishValueTranslator())(value, english, group);
}

export function enumValueTranslator(target: Messages): ValueTranslator {
  const source = SOURCE_MESSAGES.enum;
  const byValue = new Map<string, string[]>();
  for (const key of Object.keys(source)) {
    const value = key.slice(key.indexOf('.') + 1);
    byValue.set(value, [...(byValue.get(value) ?? []), key]);
  }
  return (value, english, group) => {
    const grouped = group ? `${group}.${value}` : undefined;
    if (grouped && grouped in source) return target[grouped] ?? null;
    const keys = byValue.get(value);
    if (!keys) return null;
    const key = keys.find((candidate) => source[candidate] === english) ?? keys[0];
    return key ? (target[key] ?? null) : null;
  };
}

export function setErrorTranslator(translator: ErrorTranslator | null): void {
  errorTranslator = translator;
}

export function translateServerError(messageKey: string | undefined, params?: Record<string, string | number>): string | null {
  if (!messageKey || !errorTranslator || !messageKey.startsWith('errors:')) return null;
  return errorTranslator(messageKey.slice('errors:'.length), params);
}

export function storedUiLocale(): UiLocale | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value ? normaliseUiLocale(value) : null;
  } catch {
    return null;
  }
}

export function rememberUiLocale(locale: UiLocale | null): void {
  try {
    if (locale) window.localStorage.setItem(STORAGE_KEY, locale);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
}

export function offeredLocale(value: string | null | undefined): UiLocale {
  const locale = normaliseUiLocale(value);
  return RELEASED_LOCALES[locale] ? locale : 'en';
}

export function applyDocumentLanguage(locale: UiLocale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
  document.documentElement.dir = localeDirection(locale);
}

const loaded = new Map<string, Promise<Messages>>();

export function catalogue(locale: UiLocale, namespace: Namespace): Promise<Messages> {
  const key = `${locale}:${namespace}`;
  let pending = loaded.get(key);
  if (!pending) {
    pending = loadCatalogue(locale, namespace).catch(() => ({}));
    loaded.set(key, pending);
  }
  return pending;
}

export function namespaceAllowlist(locale: UiLocale, namespace: Namespace): Set<string> {
  const prefix = `${namespace}:`;
  const entries = [...(TRANSLATION_ALLOWLIST[locale] ?? [])].filter((entry) => entry.startsWith(prefix));
  return new Set(entries.map((entry) => entry.slice(prefix.length)));
}

export function translatedOrSource(locale: UiLocale, namespace: Namespace, target: Messages): { locale: UiLocale; messages: Messages } {
  const source = SOURCE_MESSAGES[namespace];
  if (locale === 'en') return { locale: 'en', messages: source };
  const { complete } = catalogueCompleteness(source, target, namespaceAllowlist(locale, namespace));
  return complete ? { locale, messages: target } : { locale: 'en', messages: source };
}
